/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {setFleetUpdateFetching} from '../../../store/actions/tracking';

/**
 * useFleetUpdateFetchingStatus wraps a tracking2 fleet-update consumer's own
 * refetch function (the one passed to `useFleetUpdates`) so the shared
 * registry always sees it start and finish, keyed by a stable literal id
 * shared by no other consumer.
 *
 * This deliberately does NOT mirror a react-query `isFetching` boolean via
 * an effect - a fast/cached refetch can flip that boolean true then false
 * within the same render batch, so a watcher relying on `useSelector` to
 * ever observe the intermediate `true` can miss it entirely, permanently
 * stalling `AssetUpdateProgressBar`'s cycle until its safety timeout.
 * Dispatching "start" synchronously and "done" from the returned promise's
 * `.finally()` instead guarantees both are always observed, however fast
 * the refetch resolves, since a `.finally()` callback runs in a later task
 * than the call that triggered it - never the same render batch.
 *
 * ```typescript
 *
 *   import {useFleetUpdateFetchingStatus} from
 *     '@/routes/tracking2/hooks/useFleetUpdateFetchingStatus';
 *   ...
 *
 *   const trackedRefetch = useFleetUpdateFetchingStatus('geofence', refetch);
 *   useFleetUpdates(trackedRefetch, loading);
 *
 * ```
 *
 * @param key       - The stable literal id identifying this consumer.
 * @param refetchFn - The consumer's own refetch function to wrap.
 * @returns           A wrapped refetch that dispatches the fetching status.
 */
export const useFleetUpdateFetchingStatus = (
  key: string,
  refetchFn: (...args: unknown[]) => unknown
): (...args: unknown[]) => unknown => {
  const dispatch = useDispatch();

  return useCallback((...args: unknown[]) => {
    dispatch(setFleetUpdateFetching(key, true));
    return Promise.resolve(refetchFn(...args)).finally(() => {
      dispatch(setFleetUpdateFetching(key, false));
    });
  }, [dispatch, key, refetchFn]);
};

export default useFleetUpdateFetchingStatus;
